import React from 'react';
import Button from '../../../buttons/DeprecatedButton/DeprecatedButton';
import cryingMonkey from 'assets/monkey-crying.svg';
import './DowngradeReasonModal.scss';
import Input from '../../../components/Input/Input';
import { compose, withHandlers, withStateHandlers } from 'recompose';
import { StateUpdaterMethodTypes } from '@mm/utils';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';

export const swapPagesReason =
  'I upgraded the wrong page or need to transfer my subscription to a different page';
const answers = [
  "I couldn't figure it out",
  "I'm using another service",
  "Chatbots just aren't for me",
  "I couldn't get any contacts",
  "I need features MobileMonkey doesn't support",
  "I'm having technical problems",
  "I don't use it enough",
  "It's too expensive",
  'Other (please specify)',
];

const pageAnswers = answers.slice();
pageAnswers.splice(-1, 0, swapPagesReason);

type OuterProps = {
  closeModal: Function;
  onDowngradeClick: Function;
  facebookPage?: ConnectedPageModel;
  anyFreePages: boolean;
};

type PropsFromWithHandlers = {
  otherReasonIndex: ReturnType<typeof otherReasonIndex>;
  usedAnswers: ReturnType<typeof usedAnswers>;
  onChangeReason: ReturnType<typeof onChangeReason>;
  onChangeRadioReason: ReturnType<typeof onChangeRadioReason>;
  onDowngrade: ReturnType<typeof onDowngrade>;
};

type InitialState = ReturnType<typeof initialState>;
type StateUpdaters = StateUpdaterMethodTypes<
  InitialState,
  OuterProps,
  typeof stateUpdaters
>;

type InnerProps = InitialState & StateUpdaters & PropsFromWithHandlers;

type Props = InnerProps & OuterProps;

const DowngradeReasonModal = ({
  closeModal,
  leavingReason,
  onChangeReason,
  onChangeRadioReason,
  reasonIndex,
  onDowngrade,
  facebookPage,
  anyFreePages,
  otherReasonIndex,
  usedAnswers,
}: Props) => (
  <div className="downgrade-reason__modal">
    <div className="downgrade-reason__header">
      <img className="downgrade-reason__img" src={cryingMonkey} />
      <div className="downgrade-reason__title">Downgrading?</div>
      <div className="downgrade-reason__sub-title">
        Can you let us know why?
      </div>
    </div>
    <div className="downgrade-reason__body">
      <div className="downgrade-reason__answers">
        {usedAnswers().map((answer, index) => (
          <>
            <label className="downgrade-reason__choose-case" key={index}>
              <input
                type="radio"
                name="name"
                checked={index === reasonIndex}
                className="downgrade-reason__rd-btn"
                onChange={() => onChangeRadioReason(index)}
              />
              {answer}
            </label>
          </>
        ))}
      </div>
      <div className=" confirm-downgrade-modal__text downgrade-reason__text">
        <Input
          type="textarea"
          name="textarea-input"
          rows="3"
          value={leavingReason}
          onChange={onChangeReason}
        />
      </div>
    </div>
    <div className="downgrade-reason__footer">
      <Button
        invert
        superDarkGray
        disabled={reasonIndex === otherReasonIndex() && !leavingReason.trim()}
        className="downgrade-reason__btn"
        onClick={onDowngrade}
      >
        Downgrade
      </Button>
      <Button
        superDarkGray
        className="downgrade-reason__btn"
        onClick={closeModal}
      >
        Never mind
      </Button>
    </div>
  </div>
);

const onChangeReason =
  ({ setReason, setReasonIndex, otherReasonIndex }) =>
  e => {
    setReason(e.target.value);
    setReasonIndex(otherReasonIndex());
  };

const onChangeRadioReason =
  ({ setReasonIndex, setReason, otherReasonIndex }) =>
  answerIndex => {
    if (answerIndex !== otherReasonIndex()) {
      setReason('');
    }
    setReasonIndex(answerIndex);
  };

const onDowngrade =
  ({
    onDowngradeClick,
    reasonIndex,
    leavingReason,
    otherReasonIndex,
    usedAnswers,
  }) =>
  () => {
    if (reasonIndex === otherReasonIndex()) {
      return onDowngradeClick(`Other: ${leavingReason}`);
    }
    onDowngradeClick(usedAnswers()[reasonIndex]);
  };

const initialState = (props: OuterProps) => {
  return {
    leavingReason: '',
    reasonIndex: 0,
  };
};

const usedAnswers =
  ({ facebookPage, anyFreePages }) =>
  () =>
    facebookPage && anyFreePages ? pageAnswers : answers;

const otherReasonIndex =
  ({ usedAnswers }) =>
  () =>
    usedAnswers.length - 1;

const stateUpdaters = {
  setReason: () => leavingReason => ({ leavingReason }),
  setReasonIndex: () => reasonIndex => ({ reasonIndex }),
};

const enhance = compose<InnerProps, OuterProps>(
  withStateHandlers(initialState, stateUpdaters),
  withHandlers({ usedAnswers }),
  withHandlers({ otherReasonIndex }),
  withHandlers({ onChangeReason, onChangeRadioReason }),
  withHandlers({ onDowngrade }),
);

export default enhance(DowngradeReasonModal);
